import {some} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'
import {createPropPicker, has} from '@shared/helpers/object'

import {
    BankCode,
    BankCodeType,
    FinancialInvestmentCompanyCode,
    PaymentsCardCode,
    PayMethod,
    PayNextMethod,
    ProductClass,
    ProductClassType,
} from '@web/core/constants'

/**
 * 옵션추가정보의 개별 입력시 상품명을 반환합니다.
 */
export function makeAddOptionTitle({
    productClassType,
    productName = '',
    optionContents = '',
}: {
    productClassType: ProductClassType
    productName: string
    optionContents: string
}): string {
    switch (productClassType) {
        case ProductClass.OPTIONAL:
        case ProductClass.PRICING_OPTIONAL:
            return `${productName}(${optionContents})`
        case ProductClass.SUPPLEMENTED:
            return optionContents
        default:
            return productName
    }
}

export const getPayMethodName = createPropPicker<typeof PayMethod>({
    [PayMethod.SAMSUNG_PAY]: '신용카드(삼성페이)',
    [PayMethod.BANK_EASY_PAY]: '계좌 간편결제',
    [PayMethod.CARD]: '신용카드',
    [PayMethod.APPLE_PAY]: '신용카드 (Apple Pay)',
    [PayMethod.CARD_EASY_PAY]: '카드 간편결제',
    [PayMethod.CHARGE_POINT]: '네이버페이 충전포인트',
    [PayMethod.MOBILE]: '휴대폰',
    [PayMethod.MOBILE_EASY_PAY]: '휴대폰 간편결제',
    [PayMethod.REWARD_POINT]: '네이버페이 포인트',
    [PayMethod.PAY_LATER]: '후불결제',
    [PayMethod.SKIP]: '나중에 결제',
    [PayMethod.VIRTUAL_ACCOUNT]: '무통장입금',
    [PayMethod.CULTURE_LAND]: '컬쳐랜드',
    [PayMethod.HAPPY_MONEY]: '해피머니',
    [PayMethod.WELFARE_CARD]: '문화누리카드',
    [PayMethod.PAY_PAL]: 'PayPal',
    [PayMethod.MONEY_TOKEN]: 'T-money',
    [PayMethod.FAMILY_PAY]: '패밀리결제',
    [PayMethod.RECHARGE_POINT]: '',
    [PayMethod.NAVERPAY_POINT]: '',
    [PayMethod.NONE]: '',
    [PayMethod.BANK]: '',
})

export const getPayNextPayMethodName = createPropPicker<typeof PayNextMethod>({
    [PayMethod.VIRTUAL_ACCOUNT]: '무통장입금',
})

/**
 * 할인방식에 따른 할인값 단위 반환
 */
export const getDiscountValueUnit = (discountMethod: 'RATE' | 'AMOUNT') => (discountMethod === 'RATE' ? '%' : '원')

export const getCardCodeName = createPropPicker<typeof PaymentsCardCode>({
    [PaymentsCardCode.KB]: '국민',
    [PaymentsCardCode.BC]: '비씨',
    [PaymentsCardCode.SHINHAN]: '신한',
    [PaymentsCardCode.HYUNDAI]: '현대',
    [PaymentsCardCode.SAMSUNG]: '삼성',
    [PaymentsCardCode.LOTTE]: '롯데',
    [PaymentsCardCode.KEB]: '외환',
    [PaymentsCardCode.NH]: 'NH',
    [PaymentsCardCode.HANA]: '하나',
    [PaymentsCardCode.CITI]: '씨티',
    [PaymentsCardCode.KAKAO]: '카카오뱅크',
    [PaymentsCardCode.WOORI]: '우리',
    [PaymentsCardCode.GWANGJU]: '광주',
    [PaymentsCardCode.JB]: '전북',
    [PaymentsCardCode.KBANK]: '케이뱅크',
    [PaymentsCardCode.SUHYUP]: '수협',
    [PaymentsCardCode.KDB]: '산은',
    [PaymentsCardCode.JEJU]: '제주',
    [PaymentsCardCode.VISA]: '해외비자',
    [PaymentsCardCode.MASTER]: '해외마스터',
    [PaymentsCardCode.JCB]: '해외JCB',
    [PaymentsCardCode.AMEX]: '해외아멕스',
    [PaymentsCardCode.DINERS]: '해외다이너스',
    [PaymentsCardCode.DISCOVER]: '해외디스커버',
    [PaymentsCardCode.UNION]: '유니온페이(银联)',
    [PaymentsCardCode.ETC]: '기타',
    [PaymentsCardCode.IBK]: 'IBK기업은행',
    [PaymentsCardCode.BC_WOORI]: 'BC우리',
    [PaymentsCardCode.POST_INSURANCE]: '우체국예금보험',
    [PaymentsCardCode.SAVING_BANK]: '저축은행중앙회',
    [PaymentsCardCode.SAMAEUL]: '새마을금고',
    [PaymentsCardCode.TOSS_BANK]: '토스뱅크',
    [PaymentsCardCode.PAYCO]: '페이코',
})

/** 결제승인 실패에 사용되는 카드사 정보 [이름, [고객센터 번호]] */
export const getCardCompanyInfo = createPropPicker<typeof PaymentsCardCode, [string, string[]]>({
    [PaymentsCardCode.BC]: [getCardCodeName(PaymentsCardCode.BC), ['1588-4000']],
    [PaymentsCardCode.KB]: [getCardCodeName(PaymentsCardCode.KB), ['1588-1688']],
    [PaymentsCardCode.HYUNDAI]: [getCardCodeName(PaymentsCardCode.HYUNDAI), ['1577-6000']],
    [PaymentsCardCode.SAMSUNG]: [getCardCodeName(PaymentsCardCode.SAMSUNG), ['1588-8700']],
    [PaymentsCardCode.SHINHAN]: [getCardCodeName(PaymentsCardCode.SHINHAN), ['1544-7000']],
    [PaymentsCardCode.LOTTE]: [getCardCodeName(PaymentsCardCode.LOTTE), ['1588-8100']],
    [PaymentsCardCode.KEB]: [getCardCodeName(PaymentsCardCode.KEB), ['1800-1111']],
    [PaymentsCardCode.VISA]: [getCardCodeName(PaymentsCardCode.VISA), []], // KSNET 해외카드사는 비자,마스터,JCB 만 결제가능.
    [PaymentsCardCode.MASTER]: [getCardCodeName(PaymentsCardCode.MASTER), []],
    [PaymentsCardCode.JCB]: [getCardCodeName(PaymentsCardCode.JCB), []],
    [PaymentsCardCode.AMEX]: [getCardCodeName(PaymentsCardCode.AMEX), []],
    [PaymentsCardCode.DINERS]: [getCardCodeName(PaymentsCardCode.DINERS), []],
    [PaymentsCardCode.NH]: [getCardCodeName(PaymentsCardCode.NH), ['1644-4000']],
    [PaymentsCardCode.WOORI]: [getCardCodeName(PaymentsCardCode.WOORI), ['1588-9955']],
    [PaymentsCardCode.HANA]: [getCardCodeName(PaymentsCardCode.HANA), ['1800-1111']],
    [PaymentsCardCode.CITI]: [getCardCodeName(PaymentsCardCode.CITI), ['1566-1000']],
    [PaymentsCardCode.GWANGJU]: [getCardCodeName(PaymentsCardCode.GWANGJU), ['1588-4000']],
    [PaymentsCardCode.JB]: [getCardCodeName(PaymentsCardCode.JB), ['1588-4000']],
    [PaymentsCardCode.SUHYUP]: [getCardCodeName(PaymentsCardCode.SUHYUP), ['1588-4000']],
    [PaymentsCardCode.KDB]: [getCardCodeName(PaymentsCardCode.KDB), []],
    [PaymentsCardCode.JEJU]: [getCardCodeName(PaymentsCardCode.JEJU), ['1588-4000']],
    [PaymentsCardCode.DISCOVER]: [getCardCodeName(PaymentsCardCode.DISCOVER), []],
    [PaymentsCardCode.KAKAO]: [getCardCodeName(PaymentsCardCode.KAKAO), []],
    [PaymentsCardCode.KBANK]: [getCardCodeName(PaymentsCardCode.KBANK), []],
    [PaymentsCardCode.UNION]: [getCardCodeName(PaymentsCardCode.UNION), []],
    [PaymentsCardCode.ETC]: [getCardCodeName(PaymentsCardCode.ETC), []],
    [PaymentsCardCode.IBK]: [getCardCodeName(PaymentsCardCode.IBK), []],
    [PaymentsCardCode.BC_WOORI]: [getCardCodeName(PaymentsCardCode.BC_WOORI), []],
    [PaymentsCardCode.POST_INSURANCE]: [getCardCodeName(PaymentsCardCode.POST_INSURANCE), []],
    [PaymentsCardCode.SAVING_BANK]: [getCardCodeName(PaymentsCardCode.SAVING_BANK), []],
    [PaymentsCardCode.SAMAEUL]: [getCardCodeName(PaymentsCardCode.SAMAEUL), []],
    [PaymentsCardCode.TOSS_BANK]: [getCardCodeName(PaymentsCardCode.TOSS_BANK), []],
    [PaymentsCardCode.PAYCO]: [getCardCodeName(PaymentsCardCode.PAYCO), []],
})

const bankMap: Record<BankCodeType, string> = {
    [BankCode.HANKOOK]: '한국은행',
    [BankCode.KDB]: '산업은행',
    [BankCode.IBK]: '기업은행',
    [BankCode.KB_STAR]: '국민은행',
    [BankCode.SUHYUP_BANK]: '수협',
    [BankCode.NONG_HYUB]: '농협',
    [BankCode.WOORI_BANK]: '우리은행',
    [BankCode.STANDARD_CHATERED]: 'SC제일은행',
    [BankCode.CITI_BANK]: '한국시티은행',
    [BankCode.DGB]: 'iM뱅크',
    [BankCode.BUSAN_BANK]: '부산은행',
    [BankCode.KJ_BANK]: '광주은행',
    [BankCode.JEJU_BANK]: '제주은행',
    [BankCode.JB_BANK]: '전북은행',
    [BankCode.KB_BANK]: '경남은행',
    [BankCode.KFCC]: '새마을금고',
    [BankCode.CU]: '신협',
    [BankCode.EPOST_BANK]: '우체국',
    [BankCode.HANA_BANK]: '하나은행',
    [BankCode.SHINHAN]: '신한은행',
    [BankCode.K_BANK]: '케이뱅크',
    [BankCode.KAKAO]: '카카오뱅크',
    [BankCode.FSBSBISB]: 'SBI저축은행',
    [BankCode.KOREA_EXIM]: '수출입은행',
    [BankCode.LOCAL_NONG_HYUB]: '지역농.축협',
    [BankCode.FSB]: '저축은행',
    [BankCode.MS]: '모건스탠리은행',
    [BankCode.HSBC]: 'HSBC은행',
    [BankCode.DEUTSCHE]: '도이치은행',
    [BankCode.JP_MORGAN]: '제이피모간체이스은행',
    [BankCode.MIZUHO]: '미즈호코퍼레이트은행',
    [BankCode.UFJ]: '미쓰비시도쿄UFJ은행',
    [BankCode.BOA]: 'BOA',
    [BankCode.KODIT]: '신용보증기금',
    [BankCode.TECHNOLOGY]: '기술신용보증기금',
    [BankCode.TOSS]: '토스뱅크',
    [BankCode.HF]: '한국주택금융공사',
    [BankCode.SGIC]: '서울보증보험',
    [BankCode.POLICE]: '경찰청',
    [BankCode.KFTC]: '금융결제원',
}

/** 은행코드로 은행명 반환. bankMap 참조 */
export const getBankName = createPropPicker<typeof BankCode>(bankMap)

/** 입력된 bankCode 가 BankCodeType 인지 검사 */
export const isBankCode = (bankCode: string): bankCode is BankCodeType =>
    has(bankCode, BankCode) || some(eq(bankCode), BankCode)

export const getFinancialInvestmentCompanyText = createPropPicker<typeof FinancialInvestmentCompanyCode>({
    [FinancialInvestmentCompanyCode.YUANTA]: '유안타증권',
    [FinancialInvestmentCompanyCode.KBSEC]: 'KB증권',
    [FinancialInvestmentCompanyCode.SAMSUNG]: '삼성증권',
    [FinancialInvestmentCompanyCode.KOREA]: '한국투자증권',
    [FinancialInvestmentCompanyCode.NHQV]: 'NH투자증권',
    [FinancialInvestmentCompanyCode.KYOBO]: '교보증권',
    [FinancialInvestmentCompanyCode.HI]: '하이투자증권',
    [FinancialInvestmentCompanyCode.KIWOOM]: '키움증권',
    [FinancialInvestmentCompanyCode.EBEST]: '이베스트투자',
    [FinancialInvestmentCompanyCode.SK]: 'SK증권',
    [FinancialInvestmentCompanyCode.DAESHIN]: '대신증권',
    [FinancialInvestmentCompanyCode.HANAW]: '하나금융투자',
    [FinancialInvestmentCompanyCode.SHINHANINVEST]: '신한투자증권',
    [FinancialInvestmentCompanyCode.DB]: 'DB금융투자증권',
    [FinancialInvestmentCompanyCode.EUGENEFN]: '유진투자증권',
    [FinancialInvestmentCompanyCode.MERITZ]: '메리츠종합금융증권',
    [FinancialInvestmentCompanyCode.SHINYOUNG]: '신영증권',
    [FinancialInvestmentCompanyCode.FOSS]: '한국포스증권',
    [FinancialInvestmentCompanyCode.NAMUH]: 'NH나무증권',
    [FinancialInvestmentCompanyCode.CREON]: '크레온증권',
    [FinancialInvestmentCompanyCode.MIRAEASSET2]: '미래에셋증권',
    [FinancialInvestmentCompanyCode.MIRAEASSET]: '미래에셋증권',
    [FinancialInvestmentCompanyCode.HMC]: '현대차증권',
    [FinancialInvestmentCompanyCode.SOLOMON]: '메리츠증권',
    [FinancialInvestmentCompanyCode.HANWHA]: '한화증권',
})
